/**
 * SEMILLA canon de la cola HITL (cola.html, F6): el trabajo humano-en-el-
 * bucle que decide qué entra en producción — insights, activaciones,
 * precedencias, retracciones. El motor real que las detecta llega con F5b;
 * hoy son datos fijos. Fuente ÚNICA compartida entre la sidepane
 * (QueuePane: un resumen, `sidepane: true`) y la pantalla completa
 * (ColaPage: las 7) — ver comentario de cada uno para el reparto.
 */
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { buttonClass, type HitlAction } from "@skein/ui";

export type QueueLane = "p1" | "p2";
export type QueueTipo = "insight" | "activacion" | "precedencia" | "retraccion";

export type QueueActionDef =
  | { kind: "resolve"; label: string; kbd: "A" | "R"; tone: "ok" | "danger"; done: string; past: string }
  | { kind: "nav"; label: string; kbd: "E"; to: "/skills/$slug"; params: { slug: string } }
  | { kind: "defer"; label: string; kbd: "E"; toast: string };

export interface QueueCard {
  id: string; lane: QueueLane; tipo: QueueTipo;
  /** true: también aparece en el resumen de la sidepane compartida (P1 + los dos primeros P2 → 5 de 7). */
  sidepane: boolean;
  kind: ReactNode; sla: string; late?: boolean | undefined;
  title: ReactNode; titleText: string; rows: ReadonlyArray<{ k: ReactNode; v: ReactNode }>;
  /** cómo queda la entidad en el rastro de «resueltas hoy» al resolver (p. ej. «el insight <b>…</b> en <b>…</b>»). */
  ent: ReactNode;
  ok: QueueActionDef; mid?: QueueActionDef | undefined; ko?: QueueActionDef | undefined;
}

const skillLink = (slug: string) => (
  <Link to="/skills/$slug" params={{ slug }} className={buttonClass({ variant: "link" }, "sk-mono")}>{slug}</Link>
);
const casaLink = (id: string, nombre: string) => (
  <Link to="/implantaciones/$id" params={{ id }} className={buttonClass({ variant: "link" })}>{nombre}</Link>
);

export const COLA_SEED: QueueCard[] = [
  {
    id: "insight-rappels", lane: "p1", tipo: "insight", sidepane: true,
    kind: "◈ insight · skill", sla: "SLA 4h",
    title: <>Añadir pregunta de rappels al paso 1 de {skillLink("erosion-margen")}</>,
    titleText: "añadir pregunta de rappels al paso 1 de erosion-margen",
    rows: [
      { k: "evidencia", v: "5 conversaciones se estancan en el paso 1 (3 implantaciones)" },
      { k: "consecuencia", v: "se aplica al draft v2 — nada llega a producción sin publicar" },
    ],
    ent: <>el insight <b>pregunta de rappels</b> en <b>erosion-margen</b></>,
    ok: { kind: "resolve", label: "Aplicar", kbd: "A", tone: "ok", done: "Insight aplicado al draft v2.", past: "aplicó" },
    mid: { kind: "nav", label: "Editar", kbd: "E", to: "/skills/$slug", params: { slug: "erosion-margen" } },
    ko: { kind: "resolve", label: "Descartar", kbd: "R", tone: "danger", done: "Insight descartado — motivo pedido.", past: "descartó" },
  },
  {
    id: "activacion-vielsa", lane: "p1", tipo: "activacion", sidepane: true,
    kind: "⌘ activación · power-bi", sla: "SLA vencido 2h", late: true,
    title: <>{casaLink("eng-vielsa", "Grupo Vielsa")}: conexión Power BI verificada — falta registro final</>,
    titleText: "grupo vielsa: conexión power bi verificada — falta registro final",
    rows: [
      { k: "evidencia", v: "aprobada por su Client Admin · credenciales verificadas 09:12" },
      { k: "consecuencia", v: "el asesor pasa de criterio a cálculo con esa fuente (R-4)" },
    ],
    ent: <>la activación <b>Power BI · Grupo Vielsa</b></>,
    ok: { kind: "resolve", label: "Registrar", kbd: "A", tone: "ok", done: "Activación registrada en eng-vielsa.", past: "registró" },
    ko: { kind: "resolve", label: "Devolver", kbd: "R", tone: "danger", done: "Devuelta a provisión con nota.", past: "devolvió" },
  },
  {
    id: "promover-regla-33", lane: "p1", tipo: "insight", sidepane: true,
    kind: "◈ insight · conocimiento", sla: "SLA 8h",
    title: "Promover a KnowledgeItem: «regla del 33 % de dependencia» citada 8 veces",
    titleText: "promover a knowledgeitem: «regla del 33 % de dependencia» citada 8 veces",
    rows: [
      { k: "evidencia", v: "aparece en respuestas de 4 implantaciones sin fuente curada" },
      { k: "consecuencia", v: "entra en capa general con fuente citable; deja de improvisarse" },
    ],
    ent: <>la <b>regla del 33 % de dependencia</b> a KnowledgeItem</>,
    ok: { kind: "resolve", label: "Promover", kbd: "A", tone: "ok", done: "Borrador de KnowledgeItem creado.", past: "promovió" },
    ko: { kind: "resolve", label: "Descartar", kbd: "R", tone: "danger", done: "Marcada como no-curable.", past: "descartó" },
  },
  {
    id: "regla-margen-tesoreria", lane: "p2", tipo: "precedencia", sidepane: true,
    kind: "§ precedencia", sla: "sin SLA",
    title: "Decidir regla curada margen ∩ tesorería (4 casos en 30d)",
    titleText: "decidir regla curada margen ∩ tesorería (4 casos en 30d)",
    rows: [{ k: "consecuencia", v: "sustituye a la regla determinista en ese cruce" }],
    ent: <>la regla curada <b>margen ∩ tesorería</b></>,
    ok: { kind: "resolve", label: "Decidir", kbd: "A", tone: "ok", done: "Panel de regla abierto.", past: "decidió" },
    mid: { kind: "defer", label: "Posponer", kbd: "E", toast: "Pospuesta a la revisión semanal — sigue en P2." },
  },
  {
    id: "retraccion-carnicas-illa", lane: "p2", tipo: "retraccion", sidepane: true,
    kind: "△ retracción", sla: "sin SLA",
    title: <>{casaLink("eng-carnicas-illa", "Cárnicas Illa")} retiró un criterio confirmado — ¿lo indujo el skill?</>,
    titleText: "cárnicas illa retiró un criterio confirmado — ¿lo indujo el skill?",
    rows: [
      { k: "evidencia", v: "el fact nació de una propuesta de erosion-margen v1" },
      { k: "consecuencia", v: "si el patrón se repite, candidata a edición del método" },
    ],
    ent: <>la retracción de <b>Cárnicas Illa</b></>,
    ok: { kind: "resolve", label: "Revisar", kbd: "A", tone: "ok", done: "Revisión abierta con la cadena completa.", past: "revisó" },
    ko: { kind: "resolve", label: "Cerrar", kbd: "R", tone: "danger", done: "Cerrado como caso aislado.", past: "cerró" },
  },
  {
    id: "glosa-punto-muerto", lane: "p2", tipo: "insight", sidepane: false,
    kind: "◈ insight · skill", sla: "sin SLA",
    title: <>Glosa de «punto muerto» confusa en {skillLink("umbral-rentabilidad")}</>,
    titleText: "glosa de «punto muerto» confusa en umbral-rentabilidad",
    rows: [
      { k: "evidencia", v: "2 executives preguntaron qué significa" },
      { k: "consecuencia", v: "edición menor del método — draft" },
    ],
    ent: <>la glosa de <b>punto muerto</b> en <b>umbral-rentabilidad</b></>,
    ok: { kind: "resolve", label: "Aplicar", kbd: "A", tone: "ok", done: "Glosa propuesta aplicada al draft.", past: "aplicó" },
    ko: { kind: "resolve", label: "Descartar", kbd: "R", tone: "danger", done: "Descartada — motivo pedido.", past: "descartó" },
  },
  {
    id: "activacion-textil-bages", lane: "p2", tipo: "activacion", sidepane: false,
    kind: "⌘ activación · business-central", sla: "sin SLA",
    title: <>{casaLink("eng-textil-bages", "Textil Bages")}: credenciales de BC recibidas — pendiente de verificar</>,
    titleText: "textil bages: credenciales de bc recibidas — pendiente de verificar",
    rows: [{ k: "consecuencia", v: "primera fuente de la casa: el asesor estrena cálculo" }],
    ent: <>las credenciales de <b>BC · Textil Bages</b></>,
    ok: { kind: "resolve", label: "Verificar", kbd: "A", tone: "ok", done: "Verificación lanzada — resultado a la cola.", past: "verificó" },
    ko: { kind: "resolve", label: "Devolver", kbd: "R", tone: "danger", done: "Devueltas al Client Admin con nota.", past: "devolvió" },
  },
];

/** Traduce las acciones declarativas de una tarjeta a los HitlAction que pinta la tarjeta del sistema. */
export function buildActions(card: QueueCard, handlers: {
  resolve: (done: string, past: string) => void;
  navigate: (a: Extract<QueueActionDef, { kind: "nav" }>) => void;
  say: (msg: string) => void;
}): HitlAction[] {
  const one = (a: QueueActionDef): HitlAction => {
    if (a.kind === "resolve") return { label: a.label, kbd: a.kbd, tone: a.tone, onClick: () => handlers.resolve(a.done, a.past) };
    if (a.kind === "nav") return { label: a.label, kbd: a.kbd, onClick: () => handlers.navigate(a) };
    return { label: a.label, kbd: a.kbd, onClick: () => handlers.say(a.toast) };
  };
  return [one(card.ok), ...(card.mid ? [one(card.mid)] : []), ...(card.ko ? [one(card.ko)] : [])];
}
